import React, { useEffect, useState } from 'react';
import { Select, Input, Button, Form } from 'antd';
import TestEmbedModal from './TestEmbedModal';
import { useCreateAiAvatarMutation } from '../../../generated/graphql';
import styles from '../agent-sections.module.css';

export interface AIAvatarConfig {
  avatarType: string;
  llmConfig: {
    provider: string;
    model: string;
  };
  ttsConfig: {
    provider: string;
    voiceId?: string;
  };
  botDefinition: {
    botName: string;
    personality: string;
    initialMessage: string;
  };
}

const baseURL2022Global = 'https://d23hwblhugcea5.cloudfront.net/Global/';

const ERROR_CREATE_AVATAR = 'Failed to create AI Avatar';

const avatars = [
  {
    url: `${baseURL2022Global}Avatares2025/AI/Avatar_2.glb`,
    preview: `${baseURL2022Global}Avatares2025/AI/Preview/Avatar_2.jpg`,
  },
  {
    url: `${baseURL2022Global}Avatares2025/AI/Avatar_7.glb`,
    preview: `${baseURL2022Global}Avatares2025/AI/Preview/Avatar_7.jpg`,
  },
  {
    url: `${baseURL2022Global}Avatares2025/AI/Avatar_8.glb`,
    preview: `${baseURL2022Global}Avatares2025/AI/Preview/Avatar_8.jpg`,
  },
  {
    url: `${baseURL2022Global}Avatares2025/AI/Avatar_4.glb`,
    preview: `${baseURL2022Global}Avatares2025/AI/Preview/Avatar_4.jpg`,
  },
];

const llmModels = {
  groq: [
    { id: 'llama-3.1-8b-instant', name: 'Llama 3.1 Instant' },
    { id: 'llama-3.3-70b-versatile', name: 'LLama 3.1 Versatile' },
    { id: 'openai/gpt-oss-120b', name: 'GPT oss' },
    { id: 'deepseek-r1-distill-llama-70b', name: 'Deepseek R1' },
  ],
  chatgpt: [
    { id: 'gpt-4-turbo-preview', name: 'GPT-4 Turbo 128K' },
    { id: 'gpt-4', name: 'GPT-4 8K' },
    { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo 16K' },
    { id: 'gpt-3.5-turbo-0125', name: 'GPT-3.5 Turbo' },
  ],
  fanar: [{ id: 'Islamic-RAG', name: 'Islamic RAG' }],
};

enum PRESET_TYPES {
  HEALTH_COACH = 'health-coach',
  TECH_ADVISOR = 'tech-advisor',
  MATH_COMPANION = 'math-companion',
  KIDS_COACH = 'kids-coach',
  CUSTOM = 'custom',
}

const presets = [
  {
    id: PRESET_TYPES.HEALTH_COACH,
    label: 'Health Coach: "Coach Vida"',
    botName: 'Coach Vida',
    personality:
      "You are a professional yet approachable health coach named Coach Vida. You speak with clarity and positivity, helping users define and reach fitness, nutrition, and wellness goals. You always ask for relevant metrics like age, weight, activity level, and goals before giving advice. Explain things simply and avoid medical jargon. Focus on realistic, holistic lifestyle improvements. Always limit your answers to 30 words. Be motivating but not overwhelming. Break guidance into manageable steps. You're here to support, not lecture.",
    initialMessage:
      "Hi! What are your health goals? Weight, energy, fitness? Let's build your plan.",
  },
  {
    id: PRESET_TYPES.TECH_ADVISOR,
    label: 'Technology Advisor: "Ava the Avatar Pro"',
    botName: 'Ava the Avatar Pro',
    personality:
      'You are Ava, a friendly but professional tech advisor who specializes in helping users create, customize, and deploy their 3D avatars using the CREATE AI tool. You explain how to select a persona, assign a brain, choose a voice, and embed the result into any webpage. Use simple terms, no code unless asked. Always break tasks into steps. Offer help on customization, embedding, and troubleshooting. Be helpful, clear, and responsive. Limit every reply to 30 words. Avoid tech jargon unless the user requests advanced help.',
    initialMessage:
      "Want to build your own 3D avatar? I'll help you step by step.",
  },
  {
    id: PRESET_TYPES.MATH_COMPANION,
    label: 'Math Companion for Kids: "Matho"',
    botName: 'Matho',
    personality:
      "You are Matho, a playful and smart math companion for kids aged 5 to 12. You make learning math fun with games, jokes, and friendly encouragement. Use simple language and friendly tone. Always ask the child their age first to adapt the level. Never lecture—ask questions and give short, clear answers. Praise often. Don't go over 30 words per reply. You can explain concepts like addition, subtraction, multiplication, shapes, or logic games. Keep the vibe fun, positive, and full of curiosity.",
    initialMessage:
      "Hi there! How old are you? I've got cool math games just for you!",
  },
  {
    id: PRESET_TYPES.KIDS_COACH,
    label: 'Kids Passion Coach: "Spark"',
    botName: 'Spark',
    personality:
      "You are Spark, a cheerful and curious coach who helps kids discover their passions through gentle, thoughtful conversation. You ask about what they love doing, what makes them excited, and what they imagine doing in the future. You speak simply, warmly, and with lots of encouragement. Use age-appropriate questions and examples. You're never pushy—just curious. Every response is under 30 words and always includes a positive message. Guide them to explore topics like art, space, animals, tech, or sports in a natural way.",
    initialMessage:
      "Hey! What do you love doing for fun? Let's go on a discovery adventure!",
  },
  {
    id: PRESET_TYPES.CUSTOM,
    label: 'Create your own',
    botName: '',
    personality: '',
    initialMessage: '',
  },
];

const elevenLabsVoices = [
  { id: '9BWtsMINqrJLrRacOk9x', name: 'Aria' },
  { id: 'CwhRBWXzGAHq8TQ4Fs17', name: 'Roger' },
  { id: 'EXAVITQu4vr4xnSDxMaL', name: 'Sarah' },
  { id: 'FGY2WhTYpPnrIDTdsKH5', name: 'Laura' },
  { id: 'IKne3meq5aSn9XLyUdCD', name: 'Charlie' },
  { id: 'JBFqnCBsd6RMkjVDRZzb', name: 'George' },
  { id: 'N2lVS1w4EtoT3dr4eOWO', name: 'Callum' },
  { id: 'SAz9YHcvj6GT2YYXdXww', name: 'River' },
  { id: 'TX3LPaxmHKxFdv7VOQHJ', name: 'Liam' },
];

const AIAvatars = () => {
  const [selectedModel, setSelectedModel] =
    useState<React.JSX.Element | null>();
  const [form] = Form.useForm();
  const [isTestEmbedModalVisible, setIsTestEmbedModalVisible] =
    React.useState<boolean>(false);
  const [selectedAvatarIndex, setSelectedAvatarIndex] =
    React.useState<number>(0);
  const [botId, setBotId] = React.useState<string>('');
  const [createAIAvatar, { loading, error }] = useCreateAiAvatarMutation();
  const [avatarType, setAvatarType] = React.useState<string>('embed');
  const [selectedPreset, setSelectedPreset] = React.useState<string>(
    PRESET_TYPES.CUSTOM
  );

  useEffect(() => {
    const botConfig = selectedModel?.props?.asset?.metadata?.botConfig;
    if (botConfig) {
      setBotId(botConfig.botId);

      form.setFieldsValue({
        avatarType: botConfig.avatarType || 'assistant',
        llmProvider: botConfig.llmConfig.provider,
        llmModel: botConfig.llmConfig.model,
        ttsProvider: botConfig.ttsConfig.provider,
        voiceId: botConfig.ttsConfig.voiceId || '9BWtsMINqrJLrRacOk9x',
        botName: botConfig.botDefinition.botName,
        personality: botConfig.botDefinition.personality,
        initialMessage: botConfig.botDefinition.initialMessage,
      });

      setAvatarType(botConfig.avatarType || 'assistant');
      setSelectedPreset(PRESET_TYPES.CUSTOM);
    }
  }, [selectedModel, form]);

  const isCustomPresetSelected = selectedPreset === PRESET_TYPES.CUSTOM;

  const handlePresetChange = (value: string) => {
    setSelectedPreset(value);

    const preset = presets.find((p) => p.id === value);
    if (preset) {
      form.setFieldsValue({
        botName: preset.botName,
        personality: preset.personality,
        initialMessage: preset.initialMessage,
      });
    }
  };

  const onFinish = async (values) => {
    if (selectedAvatarIndex === -1) {
      console.error('Please select an avatar before creating');
      return;
    }

    const selectedAvatar = avatars[selectedAvatarIndex];
    const llmProvider = getProviderByModelId(values.llmModel);
    const payload = {
      llmProvider: llmProvider,
      llmModel: values.llmModel,
      ttsProvider: values.ttsProvider,
      botName: values.botName,
      botPersonality: values.personality,
      initialMessage: values.initialMessage,
      modelUrl: selectedAvatar.url,
      ttsVoiceId: values.ttsProvider === 'elevenlabs' ? values.voiceId : null,
    };

    try {
      const { data } = await createAIAvatar({
        variables: { data: payload },
      });

      if (data?.createAIAvatar?.status === true) {
        setBotId(data.createAIAvatar.id || '');
        setIsTestEmbedModalVisible(true);
      } else {
        console.error(ERROR_CREATE_AVATAR);
      }
    } catch (error) {
      console.error(`${ERROR_CREATE_AVATAR}: ${error}`);
    }
  };

  const handleAvatarTypeChange = (value: string) => {
    setAvatarType(value);
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <p className={styles.loadingText}>Creating AI Avatar...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <p className={styles.errorText}>
          Error creating AI Avatar: {error.message}
        </p>
      </div>
    );
  }

  const getLlmModelOptions = () => {
    const allModels = [
      ...llmModels.groq.map((model) => ({ ...model, provider: 'groq' })),
      ...llmModels.chatgpt.map((model) => ({ ...model, provider: 'chatgpt' })),
    ];
    return allModels.map((model) => ({
      value: model.id,
      label: model.name,
    }));
  };

  const getProviderByModelId = (modelId: string): string => {
    if (llmModels.groq.some((model) => model.id === modelId)) {
      return 'groq';
    }
    if (llmModels.chatgpt.some((model) => model.id === modelId)) {
      return 'chatgpt';
    }
    if (llmModels.fanar.some((model) => model.id === modelId)) {
      return 'fanar';
    }
    return 'groq';
  };

  return (
    <div className={styles.container} id='create-ai-avatar'>
      <Form
        form={form}
        layout='vertical'
        onFinish={onFinish}
        initialValues={{
          avatarType: 'embed',
          llmProvider: 'groq',
          llmModel: llmModels['groq'][1].id,
          ttsProvider: 'elevenlabs',
          voiceId: '9BWtsMINqrJLrRacOk9x',
          preset: PRESET_TYPES.CUSTOM,
        }}
      >
        <div className={styles.formSection}>
          <Form.Item name='avatarType' hidden>
            <Select />
          </Form.Item>

          <div className={styles.sectionTitle}>Choose Persona</div>
          <div className={styles.avatarGrid}>
            {avatars.map((avatar, index) => (
              <div
                key={index}
                className={`${styles.avatarCard} ${
                  selectedAvatarIndex === index ? styles.selected : ''
                }`}
                onClick={() => setSelectedAvatarIndex(index)}
              >
                <img src={avatar.preview} alt='Avatar preview' />
              </div>
            ))}
          </div>

          <div className={styles.providerSection}>
            <div>
              <div className={styles.sectionTitle}>Choose brain</div>
              <Form.Item name='llmModel'>
                <Select
                  className={styles.aiAvatarSelect}
                  options={getLlmModelOptions()}
                />
              </Form.Item>
            </div>

            <Form.Item name='ttsProvider' hidden>
              <Select />
            </Form.Item>
            <div>
              <div className={styles.sectionTitle}>Voice</div>
              <Form.Item name='voiceId'>
                <Select
                  className={styles.aiAvatarSelect}
                  options={elevenLabsVoices.map((voice) => ({
                    value: voice.id,
                    label: voice.name,
                  }))}
                />
              </Form.Item>
            </div>
          </div>

          <div className={styles.sectionTitle}>Preset</div>
          <Form.Item name='preset' initialValue={PRESET_TYPES.CUSTOM}>
            <Select
              className={styles.aiAvatarSelect}
              options={presets.map((preset) => ({
                value: preset.id,
                label: preset.label,
              }))}
              onChange={handlePresetChange}
            />
          </Form.Item>

          <div className={styles.sectionTitle}>Bot Name</div>
          <Form.Item name='botName'>
            <Input
              className={styles.botNameInput}
              placeholder='Enter bot name...'
              disabled={!isCustomPresetSelected}
            />
          </Form.Item>

          <div className={styles.textAreaSection}>
            <div>
              <div className={styles.sectionTitle}>Bot Personality</div>
              <Form.Item name='personality'>
                <Input.TextArea
                  placeholder='Enter bot personality...'
                  rows={4}
                  disabled={!isCustomPresetSelected}
                />
              </Form.Item>
            </div>

            <div>
              <div className={styles.sectionTitle}>Initial Message</div>
              <Form.Item name='initialMessage'>
                <Input.TextArea
                  placeholder='Enter initial message...'
                  rows={4}
                  disabled={!isCustomPresetSelected}
                />
              </Form.Item>
            </div>
          </div>

          <div className={styles.buttonGroup}>
            <Button
              type='primary'
              onClick={() => form.submit()}
              className={styles.createButton}
            >
              Create
            </Button>
            {botId && avatarType === 'embed' && (
              <Button
                type='default'
                onClick={() => setIsTestEmbedModalVisible(true)}
                className={styles.testEmbedButton}
              >
                Test Embed
              </Button>
            )}
          </div>
        </div>
      </Form>
      <TestEmbedModal
        isVisible={isTestEmbedModalVisible}
        setIsVisible={setIsTestEmbedModalVisible}
        botId={botId}
      />
    </div>
  );
};

export default AIAvatars;
